import { OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as i0 from "@angular/core";
var DownloadManagerService = /** @class */ (function () {
    function DownloadManagerService() {
        this.WORKER_URL = '/assets/workers/download.worker.js';
        this.WORKER_VERSION = '2.0.0';
        // Worker management
        this.workers = new Map();
        this.downloadStates = new Map();
        this.callbacks = new Map();
        // Reactive state management
        this.downloadsSubject = new BehaviorSubject(new Map());
        this.downloads$ = this.downloadsSubject.asObservable();
        // Background monitoring
        this.backgroundCheckInterval = null;
        this.isInitialized = false;
        this.initializeService();
    }
    /**
     * Initialize the service
     */
    DownloadManagerService.prototype.initializeService = function () {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
        this.startBackgroundMonitoring();
        this.checkForActiveDownloads();
        // Handle page visibility changes
        var self = this;
        if (document.addEventListener) {
            document.addEventListener('visibilitychange', function () {
                if (!document.hidden) {
                    self.checkForActiveDownloads();
                }
            });
        }
    };
    /**
     * Start a new download
     */
    DownloadManagerService.prototype.startDownload = function (backupId, url, filename, callbacks) {
        if (this.isDownloadActive(backupId)) {
            // console.log('Download already active for backup ' + backupId);
            return;
        }
        var initialState = {
            backupId: backupId,
            progress: 0,
            downloadedBytes: 0,
            totalBytes: 0,
            status: 'downloading',
            filename: filename
        };
        this.updateDownloadState(backupId, initialState);
        if (callbacks) {
            this.callbacks.set(backupId, callbacks);
        }
        this.createWorker(backupId, url, filename);
    };
    /**
     * Resume a download
     */
    DownloadManagerService.prototype.resumeDownload = function (backupId, url, filename, callbacks) {
        var existingState = this.downloadStates.get(backupId);
        var startByte = existingState ? existingState.downloadedBytes : 0;
        // console.log('Resuming download for backup ' + backupId + ' from byte ' + startByte);
        if (callbacks) {
            this.callbacks.set(backupId, callbacks);
        }
        this.createWorker(backupId, url, filename, startByte);
    };
    /**
     * Pause a download
     */
    DownloadManagerService.prototype.pauseDownload = function (backupId) {
        var worker = this.workers.get(backupId);
        if (worker) {
            worker.postMessage({ type: 'pause' });
            this.updateDownloadState(backupId, { status: 'paused' });
        }
    };
    /**
     * Cancel a download
     */
    DownloadManagerService.prototype.cancelDownload = function (backupId) {
        var worker = this.workers.get(backupId);
        if (worker) {
            worker.postMessage({ type: 'cancel' });
            worker.terminate();
            this.workers.delete(backupId);
        }
        this.updateDownloadState(backupId, { status: 'cancelled' });
        this.downloadStates.delete(backupId);
        this.callbacks.delete(backupId);
        this.notifyStateChange();
        var callbacks = this.callbacks.get(backupId);
        if (callbacks && callbacks.onCancel) {
            callbacks.onCancel(backupId);
        }
    };
    /**
     * Check if download is active
     */
    DownloadManagerService.prototype.isDownloadActive = function (backupId) {
        var state = this.downloadStates.get(backupId);
        return (state && state.status === 'downloading') || this.workers.has(backupId);
    };
    /**
     * Get download progress
     */
    DownloadManagerService.prototype.getDownloadProgress = function (backupId) {
        return this.downloadStates.get(backupId) || null;
    };
    /**
     * Get all downloads
     */
    DownloadManagerService.prototype.getAllDownloads = function () {
        return this.downloads$;
    };
    /**
     * Check for active downloads
     */
    DownloadManagerService.prototype.hasActiveDownloads = function () {
        var states = Array.from(this.downloadStates.values());
        for (var i = 0; i < states.length; i++) {
            if (states[i].status === 'downloading') {
                return true;
            }
        }
        return false;
    };
    /**
     * Get download statistics
     */
    DownloadManagerService.prototype.getDownloadStats = function () {
        var states = Array.from(this.downloadStates.values());
        var active = 0;
        var completed = 0;
        var paused = 0;
        var errors = 0;
        for (var i = 0; i < states.length; i++) {
            var state = states[i];
            if (state.status === 'downloading') {
                active++;
            }
            if (state.status === 'completed') {
                completed++;
            }
            if (state.status === 'paused') {
                paused++;
            }
            if (state.status === 'error') {
                errors++;
            }
        }
        return {
            total: states.length,
            active: active,
            completed: completed,
            paused: paused,
            errors: errors
        };
    };
    /**
     * Reset all downloads
     */
    DownloadManagerService.prototype.resetAllDownloads = function () {
        var backupIds = Array.from(this.downloadStates.keys());
        for (var i = 0; i < backupIds.length; i++) {
            this.cancelDownload(backupIds[i]);
        }
    };
    /**
     * Service cleanup
     */
    DownloadManagerService.prototype.ngOnDestroy = function () {
        this.stopBackgroundMonitoring();
        var workers = Array.from(this.workers.values());
        for (var i = 0; i < workers.length; i++) {
            workers[i].terminate();
        }
        this.workers.clear();
        this.downloadStates.clear();
        this.callbacks.clear();
    };
    // Private Methods
    /**
     * Create worker
     */
    DownloadManagerService.prototype.createWorker = function (backupId, url, filename, startByte) {
        if (startByte === void 0) { startByte = 0; }
        this.cleanupWorker(backupId);
        var workerUrl = this.WORKER_URL + '?v=' + this.WORKER_VERSION + '&t=' + Date.now();
        var worker = new Worker(workerUrl);
        this.workers.set(backupId, worker);
        var self = this;
        worker.onmessage = function (event) {
            self.handleWorkerMessage(backupId, event);
        };
        worker.onerror = function (error) {
            self.handleWorkerError(backupId, error);
        };
        worker.postMessage({
            type: 'start',
            url: url,
            filename: filename,
            start: startByte,
            backupId: backupId
        });
    };
    /**
     * Handle worker messages
     */
    DownloadManagerService.prototype.handleWorkerMessage = function (backupId, event) {
        var data = event.data;
        if (data.type === 'progress') {
            this.handleProgressUpdate(backupId, data);
        }
        else if (data.type === 'complete') {
            this.handleDownloadComplete(backupId, data);
        }
        else if (data.type === 'status') {
            this.handleStatusUpdate(backupId, data);
        }
        else if (data.type === 'paused') {
            this.handleDownloadPaused(backupId, data);
        }
        else if (data.type === 'cancelled') {
            this.handleDownloadCancelled(backupId);
        }
        else if (data.progress !== undefined) {
            // Legacy format
            this.handleProgressUpdate(backupId, data);
        }
        else if (data.success === true) {
            this.handleDownloadComplete(backupId, data);
        }
        else if (data.success === false) {
            this.handleDownloadError(backupId, data.error);
        }
    };
    /**
     * Handle progress updates
     */
    DownloadManagerService.prototype.handleProgressUpdate = function (backupId, data) {
        var updateData = {
            progress: data.progress || 0,
            downloadedBytes: data.downloadedBytes || 0,
            totalBytes: data.total || 0,
            status: 'downloading'
        };
        this.updateDownloadState(backupId, updateData);
        var callbacks = this.callbacks.get(backupId);
        if (callbacks && callbacks.onProgress) {
            var currentState = this.downloadStates.get(backupId);
            if (currentState) {
                callbacks.onProgress(currentState);
            }
        }
    };
    /**
     * Handle download completion
     */
    DownloadManagerService.prototype.handleDownloadComplete = function (backupId, data) {
        // Check if we should use native download for large files
        if (data.useNativeDownload) {
            // console.log('Using native download for large file');
            // Use browser's native download mechanism
            var link = document.createElement('a');
            link.href = data.downloadUrl;
            link.download = data.filename;
            link.style.display = 'none';
            // Add proper attributes
            link.setAttribute('rel', 'noopener');
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        else if (data.chunk) {
            // Regular blob download for smaller files
            if (data.chunk.size === 0) {
                // console.error('Received empty blob for download');
                this.handleDownloadError(backupId, 'Downloaded file is empty');
                return;
            }
            this.triggerBrowserDownload(data.chunk, data.filename);
        }
        this.updateDownloadState(backupId, {
            status: 'completed',
            progress: 100
        });
        this.cleanupWorker(backupId);
        var callbacks = this.callbacks.get(backupId);
        if (callbacks && callbacks.onComplete) {
            callbacks.onComplete(backupId);
        }
        var self = this;
        setTimeout(function () {
            self.downloadStates.delete(backupId);
            self.callbacks.delete(backupId);
            self.notifyStateChange();
        }, 5000);
    };
    /**
     * Handle download errors
     */
    DownloadManagerService.prototype.handleDownloadError = function (backupId, error) {
        this.updateDownloadState(backupId, {
            status: 'error',
            error: error || 'Unknown download error',
            canResume: true
        });
        this.cleanupWorker(backupId);
        var callbacks = this.callbacks.get(backupId);
        if (callbacks && callbacks.onError) {
            callbacks.onError(backupId, error);
        }
    };
    /**
     * Handle status updates
     */
    DownloadManagerService.prototype.handleStatusUpdate = function (backupId, data) {
        if (data.isActive) {
            this.updateDownloadState(backupId, {
                downloadedBytes: data.downloadedBytes,
                totalBytes: data.total,
                progress: data.total ? Math.floor((data.downloadedBytes / data.total) * 100) : 0,
                status: 'downloading'
            });
        }
        else if (data.canResume) {
            this.updateDownloadState(backupId, {
                downloadedBytes: data.downloadedBytes,
                totalBytes: data.total,
                progress: data.total ? Math.floor((data.downloadedBytes / data.total) * 100) : 0,
                status: 'paused',
                canResume: true
            });
        }
    };
    /**
     * Handle download paused
     */
    DownloadManagerService.prototype.handleDownloadPaused = function (backupId, data) {
        this.updateDownloadState(backupId, {
            status: 'paused',
            downloadedBytes: data.downloadedBytes || 0,
            canResume: true
        });
        this.cleanupWorker(backupId);
    };
    /**
     * Handle download cancelled
     */
    DownloadManagerService.prototype.handleDownloadCancelled = function (backupId) {
        this.updateDownloadState(backupId, { status: 'cancelled' });
        this.cleanupWorker(backupId);
    };
    /**
     * Handle worker errors
     */
    DownloadManagerService.prototype.handleWorkerError = function (backupId, error) {
        console.error('Worker error for backup ' + backupId + ':', error);
        this.handleDownloadError(backupId, error.message || 'Worker error');
    };
    /**
     * Trigger browser download
     */
    DownloadManagerService.prototype.triggerBrowserDownload = function (blob, filename) {
        var objectUrl = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(function () {
            URL.revokeObjectURL(objectUrl);
        }, 1000);
    };
    /**
     * Update download state
     */
    DownloadManagerService.prototype.updateDownloadState = function (backupId, updateData) {
        var existingState = this.downloadStates.get(backupId);
        var newState = Object.assign({}, existingState, updateData, { backupId: backupId });
        this.downloadStates.set(backupId, newState);
        this.notifyStateChange();
    };
    /**
     * Notify state changes
     */
    DownloadManagerService.prototype.notifyStateChange = function () {
        this.downloadsSubject.next(new Map(this.downloadStates));
    };
    /**
     * Clean up worker
     */
    DownloadManagerService.prototype.cleanupWorker = function (backupId) {
        var worker = this.workers.get(backupId);
        if (worker) {
            worker.terminate();
            this.workers.delete(backupId);
        }
    };
    /**
     * Check for active downloads
     */
    DownloadManagerService.prototype.checkForActiveDownloads = function () {
        var entries = Array.from(this.workers.entries());
        for (var i = 0; i < entries.length; i++) {
            var entry = entries[i];
            var backupId = entry[0];
            var worker = entry[1];
            worker.postMessage({ type: 'queryState' });
        }
        this.checkIndexedDBForOrphanedDownloads();
    };
    /**
     * Check IndexedDB for orphaned downloads
     */
    DownloadManagerService.prototype.checkIndexedDBForOrphanedDownloads = function () {
        try {
            var tempWorkerUrl = this.WORKER_URL + '?v=' + this.WORKER_VERSION + '&t=' + Date.now();
            var tempWorker_1 = new Worker(tempWorkerUrl);
            var self_1 = this;
            tempWorker_1.onmessage = function (event) {
                var data = event.data;
                if (data.type === 'status' && data.canResume && !self_1.workers.has(data.backupId)) {
                    self_1.updateDownloadState(data.backupId, {
                        downloadedBytes: data.downloadedBytes,
                        totalBytes: data.total,
                        progress: data.total ? Math.floor((data.downloadedBytes / data.total) * 100) : 0,
                        status: 'paused',
                        canResume: true
                    });
                }
                tempWorker_1.terminate();
            };
            tempWorker_1.onerror = function () {
                tempWorker_1.terminate();
            };
            tempWorker_1.postMessage({ type: 'queryState' });
        }
        catch (error) {
            console.error('Error checking for orphaned downloads:', error);
        }
    };
    /**
     * Start background monitoring
     */
    DownloadManagerService.prototype.startBackgroundMonitoring = function () {
        if (this.backgroundCheckInterval) {
            return;
        }
        var self = this;
        this.backgroundCheckInterval = setInterval(function () {
            if (!document.hidden) {
                self.checkForActiveDownloads();
            }
        }, 30000);
    };
    /**
     * Stop background monitoring
     */
    DownloadManagerService.prototype.stopBackgroundMonitoring = function () {
        if (this.backgroundCheckInterval) {
            clearInterval(this.backgroundCheckInterval);
            this.backgroundCheckInterval = null;
        }
    };
    DownloadManagerService.ngInjectableDef = i0.defineInjectable({ factory: function DownloadManagerService_Factory() { return new DownloadManagerService(); }, token: DownloadManagerService, providedIn: "root" });
    return DownloadManagerService;
}());
export { DownloadManagerService };
